import { type Share } from '@solink/vd-data-layer/api-clients/platform/platform';
import { CAMERA_STREAM, SHARE_STATUS_DISABLED } from 'vd-constants';
import { type SavePanelCamera } from '../types';

/**
 * Two policy questions the save panel's detail form asks about a clip's recipients and
 * cameras. Both skins ask them, so both call these functions.
 *
 * These live apart from `saveClipUtils.ts` because they need nothing but constants and
 * types, while that module reaches `axios`, the VMS clients, `SystemReport` and the event
 * filter store. A component test that wants the real answers can import this file without
 * standing up any of that.
 */

/** Summarise the recipients of a clip.
 * `hasSharedAny` is true when at least one recipient other than the originator
 * is still shared with.
 * `recipientsCount` counts every recipient other than the originator, shared or not.
 */
export function getShareSummary(shares: Share[]) {
  const hasSharedAny = !!shares.find(
    ({ email, sharedBy, status }) => email !== sharedBy && status !== SHARE_STATUS_DISABLED,
  );

  let recipientsCount = 0;

  shares.forEach(({ sharedBy, email }) => {
    if (email !== sharedBy) {
      recipientsCount++;
    }
  });

  return { hasSharedAny, recipientsCount };
}

/*
 * The motion only checkbox will available if
 * A) It's a clip that was already shared as HD with at least one camera with two streams
 * B) If it's not already shared, there is at least one camera with two streams
 * C) It has a stream with motionless retention enabled
 */
export function shouldOfferMotionOnlyVideo({
  cameras,
  isSharedEvent,
  isBestQuality,
}: {
  cameras: SavePanelCamera[];
  isSharedEvent: boolean;
  isBestQuality: boolean;
}) {
  if (isSharedEvent || !isBestQuality) return false;
  for (const camera of cameras) {
    if (
      camera.streams.length > 1 &&
      camera.streams.some(
        (stream) => stream.enableMotionlessRetention && stream.name === CAMERA_STREAM.HD,
      )
    ) {
      return true;
    }
  }
  return false;
}
